import { Children, useEffect } from 'react';
import { css, cx } from '@linaria/atomic';
import { getBaseFontSize } from '@frontend/theme-original';
import { Text } from '../../../../components';
import { AnimatedMenu } from '../../../flyouts';
import { StyledListboxList, useListboxContext, useListboxSearch } from '../../../listbox';
import { useDropdownContext } from '../../providers';
import { DropdownInputStyles } from '../dropdown/input-styles';
import { useDropdownSearch } from '../dropdown/use-dropdown-search';

const emptyStateTextStyle1 = css`
  margin: 16px;
`;

const multiselectMenuSearchFieldStyle4 = css`
  margin: 8px 16px 0px;
`;

const multiselectMenuStyledListboxListWithSearchStyle5 = css`
  margin: 8px 0px 4px;
`;

const multiselectMenuStyledListboxListNoSearchStyle5 = css`
  margin: 0px 0px 4px;
`;

const animatedMenuFlexStyle = css`
  display: flex;
  flex-direction: column;
`;

const BASE = getBaseFontSize();

const animatedMenuMaxHeightStyle = css`
  max-height: ${288 / BASE}rem;
`;

export const EmptyState = ({ value }: { value?: string }) => {
  return (
    <Text as='li' className={emptyStateTextStyle1}>
      {!!value ? `No results found for "${value}"` : 'No results found'}
    </Text>
  );
};

export const MultiselectMenu = ({ active, children, direction, name = '', placeholder = 'Search', ...rest }) => {
  const { debouncedBlur, debouncedFocus, menuRef } = useDropdownContext();
  const { listboxProps, setActiveItem } = useListboxContext();
  const { searchFieldProps, SearchField, matchingChildren } = useListboxSearch(children);
  const searchFieldName = `ms-search-${name}`;
  const showSearch = Children.toArray(children)?.length >= 10;
  const dropdownMenuStyle = DropdownInputStyles.dropdownMenuStyle({
    active,
    direction,
  });
  const childrenArray = matchingChildren as [];
  const keydownSearch = useDropdownSearch({
    children,
    // @ts-ignore types of KeyboardEvent<HTMLUListElement> and KeyboardEvent<Element> no longer match
    onKeyDown: listboxProps.onKeyDown,
    onMatch: setActiveItem,
  });

  useEffect(() => {
    if (active) {
      // make sure to reset field value between menu toggles
      searchFieldProps.onChange({ name: searchFieldName, value: '' });
    }
  }, [active]);

  return (
    // @ts-ignore some issue with HTMLAttributes on FlyoutProps
    <AnimatedMenu
      as='div'
      className={cx(dropdownMenuStyle, animatedMenuFlexStyle, showSearch && animatedMenuMaxHeightStyle)}
      {...rest}
      ref={menuRef}
    >
      {showSearch && (
        <div className={multiselectMenuSearchFieldStyle4}>
          <SearchField
            {...searchFieldProps}
            onBlur={() => {
              debouncedBlur();
              searchFieldProps.onBlur();
            }}
            onFocus={() => {
              debouncedFocus();
              searchFieldProps.onFocus();
            }}
            label=''
            placeholder={placeholder}
            name={searchFieldName}
            tabIndex={active ? undefined : -1}
          />
        </div>
      )}

      <StyledListboxList
        {...listboxProps}
        className={
          showSearch ? multiselectMenuStyledListboxListWithSearchStyle5 : multiselectMenuStyledListboxListNoSearchStyle5
        }
        onKeyDown={showSearch ? listboxProps.onKeyDown : keydownSearch}
        tabIndex={active ? 0 : -1}
      >
        {childrenArray?.length ? childrenArray : <EmptyState value={searchFieldProps.value} />}
      </StyledListboxList>
    </AnimatedMenu>
  );
};
